import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useNavigate } from "react-router-dom";

export function QrScannerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const handledRef = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    handledRef.current = false;
    setError("");

    const scanner = new Html5Qrcode("stock-qr-reader", {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      verbose: false,
    });
    scannerRef.current = scanner;

    const openCamera = async () => {
      try {
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (width, height) => {
              const size = Math.floor(Math.min(width, height) * 0.72);
              return { width: size, height: size };
            },
            aspectRatio: 1,
          },
          (decodedText) => {
            if (handledRef.current) return;
            let itemId = "";
            try {
              const decodedUrl = new URL(decodedText, window.location.origin);
              if (decodedUrl.origin === window.location.origin) {
                itemId = decodedUrl.pathname.match(/^\/item\/(\d+)\/?$/)?.[1] ?? "";
              }
            } catch {
              itemId = decodedText.match(/^\d+$/)?.[0] ?? "";
            }

            if (!itemId) {
              setError("Ce QR code ne correspond pas à un article du stock STC.");
              return;
            }

            handledRef.current = true;
            scanner.stop()
              .catch(() => undefined)
              .finally(() => {
                if (!cancelled) {
                  onClose();
                  navigate(`/item/${itemId}`);
                }
              });
          },
          () => undefined,
        );
      } catch {
        if (!cancelled) {
          setError("La caméra n’a pas pu s’ouvrir. Autorisez l’accès à la caméra dans les réglages du navigateur, puis réessayez.");
        }
      }
    };

    void openCamera();

    return () => {
      cancelled = true;
      const activeScanner = scannerRef.current;
      scannerRef.current = null;
      if (activeScanner?.isScanning) {
        void activeScanner.stop().catch(() => undefined).finally(() => activeScanner.clear());
      } else {
        activeScanner?.clear();
      }
    };
  }, [open, navigate, onClose]);

  if (!open) return null;

  return (
    <div className="scanner-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="scanner-modal" role="dialog" aria-modal="true" aria-labelledby="scanner-title">
        <div className="scanner-head">
          <div>
            <p className="eyebrow">Caméra arrière</p>
            <h2 id="scanner-title">Scanner un QR code</h2>
          </div>
          <button type="button" className="scanner-close" onClick={onClose} aria-label="Fermer le scanner">×</button>
        </div>
        <div className="scanner-view">
          <div id="stock-qr-reader" />
          <span className="scanner-corners" aria-hidden="true" />
        </div>
        <p className="scanner-help">Placez le QR code de l’armoire dans le carré. La fiche de l’article s’ouvrira automatiquement.</p>
        {error && <div className="notice error scanner-error">{error}</div>}
      </section>
    </div>
  );
}
